import moment from 'moment-timezone';
import { FORMATS } from '../core/formats';
import { isValidScheduleDate, isValidScheduleTime } from '../schedule-field-input';
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Normalize to YYYY-MM-DD (handles ISO strings from storage). */
export function normalizeFeeDateString(value) {
    if (!value)
        return '';
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        return trimmed.slice(0, 10);
    }
    return trimmed;
}
export function normalizeFeeDateSlot(slot) {
    const fromDate = normalizeFeeDateString(slot.fromDate);
    const toDate = normalizeFeeDateString(slot.toDate || slot.fromDate) || fromDate;
    return {
        ...slot,
        fromDate,
        toDate,
    };
}
function isValidStoredFeeDateSlot(slot) {
    if (!slot.fromDate || !isValidScheduleDate(slot.fromDate)) {
        return false;
    }
    const toDate = slot.toDate || slot.fromDate;
    if (!isValidScheduleDate(toDate)) {
        return false;
    }
    if (moment(toDate, FORMATS.moment.date, true).isBefore(moment(slot.fromDate, FORMATS.moment.date, true), 'day')) {
        return false;
    }
    if (slot.h24) {
        return true;
    }
    if (!slot.open || !slot.close) {
        return false;
    }
    if (!isValidScheduleTime(slot.open) || !isValidScheduleTime(slot.close)) {
        return false;
    }
    return true;
}
export function getValidSpecificDates(fee) {
    return (fee.specific_dates || [])
        .map(normalizeFeeDateSlot)
        .filter((slot) => !!slot.fromDate && isValidStoredFeeDateSlot(slot));
}
export function isFeeScheduledBySpecificDates(fee) {
    return getValidSpecificDates(fee).length > 0;
}
/**
 * Dashboard-side validation for `specific_dates`. Returns a message for the first problem
 * found, or null when every slot is well-formed. Overnight windows (close <= open) are valid.
 */
export function validateFeeSpecificDates(slots) {
    for (const [index, raw] of (slots || []).entries()) {
        const slot = normalizeFeeDateSlot(raw);
        const n = index + 1;
        if (!slot.fromDate) {
            return `Specific date #${n}: a start date is required.`;
        }
        const from = moment(slot.fromDate, FORMATS.moment.date, true);
        const to = moment(slot.toDate, FORMATS.moment.date, true);
        if (!from.isValid() || !to.isValid()) {
            return `Specific date #${n}: dates must be valid YYYY-MM-DD values.`;
        }
        if (to.isBefore(from, 'day')) {
            return `Specific date #${n}: the end date must not be before the start date.`;
        }
        if (slot.h24) {
            continue;
        }
        if (!slot.open || !slot.close) {
            return `Specific date #${n}: enter both an open and close time, or select 24 hours.`;
        }
        if (!HHMM_RE.test(slot.open) || !HHMM_RE.test(slot.close)) {
            return `Specific date #${n}: times must be in HH:mm (24h) format.`;
        }
    }
    return null;
}
/**
 * Whether `dt` falls inside the window starting on calendar day `dayStr` (YYYY-MM-DD).
 * A close at or before open is treated as an overnight window ending the next day.
 */
function withinWindowStartingOn(dayStr, open, close, dt, timezone) {
    const start = moment.tz(`${dayStr} ${open}`, FORMATS.moment.datetime, timezone);
    const end = moment.tz(`${dayStr} ${close}`, FORMATS.moment.datetime, timezone);
    if (!start.isValid() || !end.isValid()) {
        return false;
    }
    if (!end.isAfter(start)) {
        end.add(1, 'day');
    }
    return dt.isSameOrAfter(start) && dt.isSameOrBefore(end);
}
export function feeSpecificDateSlotApplies(slot, dt, timezone) {
    const normalized = normalizeFeeDateSlot(slot);
    if (!normalized.fromDate) {
        return false;
    }
    const fromDate = moment.tz(normalized.fromDate, FORMATS.moment.date, timezone).startOf('day');
    const toDate = moment.tz(normalized.toDate, FORMATS.moment.date, timezone).endOf('day');
    if (!fromDate.isValid() || !toDate.isValid()) {
        return false;
    }
    const inRange = (day) => day.isBetween(fromDate, toDate, 'day', '[]');
    // Never mutate the caller's moment (locale / tz); work on clones.
    const today = dt.clone().tz(timezone).locale('en');
    const yesterday = today.clone().subtract(1, 'day');
    if (normalized.h24 || !normalized.open || !normalized.close) {
        return inRange(today);
    }
    const todayStr = today.format(FORMATS.moment.date);
    if (inRange(today) && withinWindowStartingOn(todayStr, normalized.open, normalized.close, dt, timezone)) {
        return true;
    }
    // Overnight spill: a window that opened yesterday (inside the date range) may still be running.
    const yesterdayStr = yesterday.format(FORMATS.moment.date);
    return inRange(yesterday) && withinWindowStartingOn(yesterdayStr, normalized.open, normalized.close, dt, timezone);
}
export function feeRecurringHoursApplies(fee, dt, timezone, isTimeWithin) {
    if (!fee.hours || fee.hours.length === 0 || !dt || !dt.isValid()) {
        return false;
    }
    return isTimeWithin({
        dt: dt.clone(), // isTimeWithin sets locale in place; keep the caller's moment untouched
        hours: fee.hours,
        last_offset: 0,
        first_offset: 0,
        timezone,
    }).isWithin;
}
export function getOrderDueDatetime(args) {
    if (args.due === 'now') {
        return args.momentNow();
    }
    if (args.due !== 'later') {
        return null;
    }
    const date = (args.date || '').trim();
    const time = (args.time || '').trim();
    if (!date || !time) {
        return null;
    }
    const dt = args.momentFromFormat(`${date} ${time}`, FORMATS.moment.datetime);
    return dt.isValid() ? dt : null;
}
/**
 * Hard gate: when a fee has `specific_dates`, the order due time must fall inside one of them.
 * Fees without specific dates pass. Recurring `hours` are NOT part of this gate — they are an
 * ordinary condition (see `feeApplies`) so existing "any"-mode fees keep their behaviour.
 */
export function feeSpecificDatesMatch(args) {
    const specificDates = getValidSpecificDates(args.fee);
    if (specificDates.length === 0) {
        return true;
    }
    if (!args.dueDatetime || !args.dueDatetime.isValid()) {
        return false;
    }
    return specificDates.some(slot => feeSpecificDateSlotApplies(slot, args.dueDatetime, args.timezone));
}
/**
 * Single source of truth for "does this fee apply to this order" — used by the API
 * (`FeeCalculate.fees`) and the storefront cart so totals can never diverge.
 *
 * Semantics (unchanged from the legacy implementation for payments/services/order_times/hours):
 * - `match_condition === 'any'`: the fee applies as soon as ONE configured condition matches.
 * - otherwise ('all'): every configured condition must match.
 * - a fee with no configured conditions always applies.
 * - `specific_dates`, when present, is an additional hard gate evaluated first.
 */
export function feeApplies(args) {
    const { fee } = args;
    if (fee.disabled) {
        return false;
    }
    if (!feeSpecificDatesMatch({ fee, dueDatetime: args.dueDatetime, timezone: args.timezone })) {
        return false;
    }
    let conditions = 0;
    let matched = 0;
    const matchAny = fee.match_condition === 'any';
    if ((fee.payments || []).length > 0) {
        conditions++;
        // IF THE PAYMENT MUST BE MATCHED, THEN DROP FEE UNTIL A PAYMENT METHOD IS SELECTED
        if (!args.payment && !matchAny) {
            return false;
        }
        else if (args.payment && fee.payments.indexOf(args.payment) !== -1) {
            matched++;
            if (matchAny)
                return true;
        }
    }
    if ((fee.services || []).length > 0) {
        conditions++;
        if (args.service && fee.services.indexOf(args.service) !== -1) {
            matched++;
            if (matchAny)
                return true;
        }
    }
    if ((fee.order_times || []).length > 0) {
        conditions++;
        if (args.due && fee.order_times.indexOf(args.due) !== -1) {
            matched++;
            if (matchAny)
                return true;
        }
    }
    if ((fee.hours || []).length > 0) {
        conditions++;
        if (feeRecurringHoursApplies(fee, args.dueDatetime, args.timezone, args.isTimeWithin)) {
            matched++;
            if (matchAny)
                return true;
        }
    }
    // IF CONDITIONS MET = CONDITIONS MATCHED, THEN FEE APPLIES
    return conditions === matched;
}
//# sourceMappingURL=fee-schedule.js.map